// @flow
/* eslint-disable react/no-danger */
import * as React from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { APP_CONFIG } from 'common/constants';
import { MAKO_ANALYTICS_PAGE_IDS, ROOT_ROUTE, IS_DEVEL } from 'client/constants';

import { Responsive, RabbiCredit, MakoAnalytics, BottomLink } from 'client/components/common';
import Socials from 'client/components/common/Socialls';
import { type EnhancedProps, withTransition } from 'client/components/common/HOCs/with-transition';
import { fileLink } from 'common/utils/file-link';
import { analytics } from 'common/utils/ga';
import { saveShareResult } from 'client/actions';
import UserForm from './UserForm';
import EmailForm from './EmailForm';

import * as css from './Final.scss';

type Props = {|
	...EnhancedProps,
	className?: string,
	in: boolean,
	checklistInfo: ChecklistsItem,
	selectedItems: Array<GalleryItemInfo>,
	saveShareResult: typeof saveShareResult,
	parentURL: string,
	location: RouterLocation,
|};

class Final extends React.PureComponent<Props> {
	transitionCompleteTimeout: TimeoutID;

	static defaultProps = {
		className: '',
	};

	componentDidMount() {
		const { onTransitionComplete, selectedItems } = this.props;
		this.transitionCompleteTimeout = setTimeout(onTransitionComplete, parseInt(css.transitionIn, 10));
		// const title = this.renderTitle();
		this.renderShareResult();
		const selectedItemsString = [];
		_.forEach(selectedItems, item => {
			selectedItemsString.push(item.title);
		});
		if (!IS_DEVEL) {
			// const location = `${APP_CONFIG.domains.client.hostname}${this.props.location.pathname}`;
			const location = `${APP_CONFIG.domains.client.hostname}/result`;
			analytics.gtag.event('page_view', {
				page_title: 'result',
				page_location: `${location}`,
				page_path: `/result`,
			});
		}
		if (this.renderRangeText()) {
			const rangeText = this.renderRangeText();
			analytics.gtag.event('Results', { CUSTOM_PARAMETER: rangeText });
		} else {
			const selectedItemsText = selectedItemsString.join(', ');
			analytics.gtag.event('Results', { CUSTOM_PARAMETER: selectedItemsText });
		}

		if (Responsive.isMatching(Responsive.MOBILE)) {
			window.scrollTo(0, 0);
		}
	}

	componentDidUpdate(prevProps: Props) {
		if (this.props.in !== prevProps.in) {
			const { onTransitionComplete } = this.props;
			let duration = parseInt(this.props.in ? css.transitionIn : css.transitionOut, 10);

			if (!this.props.in) {
				duration = 0;
			}
			clearTimeout(this.transitionCompleteTimeout);
			this.transitionCompleteTimeout = setTimeout(onTransitionComplete, duration);
		}
	}

	emailData = () => {
		const { checklistInfo, selectedItems } = this.props;
		const galleryItems = _.get(checklistInfo, 'items', []);

		const selectedIds = _.map(selectedItems, 'id');
		const notSelectedIds = _.difference(galleryItems, selectedIds);

		return {
			notSelectedIds,
			checklistData: {
				checklistId: _.get(checklistInfo, 'id'),
			},
		};
	};

	renderShareResult = () => {
		const { checklistInfo, selectedItems } = this.props;
		const isMultiple = _.get(checklistInfo, 'general.isMultiple', '0');
		const galleryItems = _.get(checklistInfo, 'items', []);
		const allItemsLength = _.size(galleryItems);
		const selectedItemsLength = _.size(selectedItems);
		const singleItemName = _.get(selectedItems[0], 'title', '');
		const maxItemsNumberSubstr = '{max_items_number}';
		const userChosenItemsNumberSubstr = '{user_number}';
		const singleItemNameSubstr = '{item_name}';
		const shareText = _.get(checklistInfo, 'form.shareResultsText', '');
		let shareTextEdited;
		if (isMultiple === '0') {
			const itemId = _.get(selectedItems[0], 'id', '');
			shareTextEdited = shareText
				.replace(`${userChosenItemsNumberSubstr}`, selectedItemsLength)
				.replace(`${maxItemsNumberSubstr}`, allItemsLength)
				.replace(`${singleItemNameSubstr}`, singleItemName);

			this.props.saveShareResult({
				description: shareTextEdited,
				total: allItemsLength,
				selected: selectedItemsLength,
				itemId,
			});
		} else {
			shareTextEdited = shareText
				.replace(`${userChosenItemsNumberSubstr}`, selectedItemsLength)
				.replace(`${maxItemsNumberSubstr}`, allItemsLength);

			this.props.saveShareResult({
				description: shareTextEdited,
				total: allItemsLength,
				selected: selectedItemsLength,
				itemId: '',
			});
		}
	};

	renderTitle = () => {
		const { checklistInfo, selectedItems } = this.props;
		const galleryItems = _.get(checklistInfo, 'items', []);
		const allItemsLength = _.size(galleryItems);
		const selectedItemsLength = _.size(selectedItems);
		const maxItemsNumberSubstr = '{max_items_number}';
		const userChosenItemsNumberSubstr = '{user_number}';
		const titleText = _.get(checklistInfo, 'form.formTitle', '');
		const titleTextEdited = titleText
			.replace(`${userChosenItemsNumberSubstr}`, selectedItemsLength)
			.replace(`${maxItemsNumberSubstr}`, allItemsLength);

		return titleTextEdited;
	};

	renderSingleTitle = () => {
		const { checklistInfo, selectedItems } = this.props;

		const titleText = _.get(checklistInfo, 'form.singleResultText', '');
		const itemNameSubstr = '{item_name}';
		const itemName = _.get(selectedItems[0], 'title', '');
		const titleTextEdited = titleText.replace(`${itemNameSubstr}`, itemName);

		return titleTextEdited;
	};

	renderRangeText = () => {
		const { checklistInfo, selectedItems } = this.props;
		const selectedItemsLength = _.size(selectedItems);

		const rangesInfo = _.get(checklistInfo, 'form.rangesInfo', []);

		const neededRange = rangesInfo.find(rangeInfo => {
			return _.inRange(selectedItemsLength, rangeInfo.range[0], rangeInfo.range[1] + 1);
		});

		return neededRange?.text;
	};

	onBottomBannerClick = () => {};

	render() {
		const { className, transitionState, checklistInfo, parentURL, location } = this.props;
		const backgroundImageDesktop = _.get(checklistInfo, 'graphics.galleryBackground', {});
		const backgroundImageMobile = _.get(checklistInfo, 'graphics.galleryBackgroundMobile', {});
		const finalLogo = _.get(checklistInfo, 'main.checklistLogo', {});
		const isMultiple = Boolean(Number(_.get(checklistInfo, 'general.isMultiple', '0')));
		const textsColor = _.get(checklistInfo, 'graphics.mainSubTitleColor', '#fff');
		const numberOfChoices = _.get(checklistInfo, 'general.numberOfChoices');
		const isSingle = !isMultiple && numberOfChoices !== 0;
		const isSharesShow = _.get(checklistInfo, 'form.enableShareResults', false);
		const sharesColor = _.get(checklistInfo, 'graphics.resultShareButtonIconColor', '#fff');

		const isShowUserForm = _.get(checklistInfo, 'form.enableFormBox', false);
		const isShowEmailForm = _.get(checklistInfo, 'form.enableMailBox', false);

		const bannerImg = _.get(checklistInfo, 'form.bannerImage', {});
		const bannerLink = _.get(checklistInfo, 'form.bannerLink', '');

		const bannerText = _.get(checklistInfo, 'form.bannerText', '');

		const cooladataChannelId = _.get(checklistInfo, 'general.cooladataChannelID', '');
		const cooladataId = _.get(checklistInfo, 'general.cooladataID', '');

		// const singleChoiceTitle = _.get(checklistInfo, 'form.singleResultText', '');
		const sharesTitle = 'שתפו עם חברים:';

		const siteName = _.get(checklistInfo, 'general.url', '');
		const siteLink = `${ROOT_ROUTE.replace(':id', siteName)}`;

		const rangesInfo = _.get(checklistInfo, 'form.rangesInfo', []);

		const resultsTextsColor = _.get(checklistInfo, 'form.resultsTextsColor', '#000');

		const shareTitle = _.get(checklistInfo, 'general.shareTitle', '');

		return (
			<div className={classNames(css.final, css[transitionState], className)}>
				{Responsive.isMatching(Responsive.DESKTOP) && (
					<div
						className={css.bgDesktop}
						style={{ backgroundImage: `url('${fileLink(backgroundImageDesktop)}')` }}
					/>
				)}
				{Responsive.isMatching(Responsive.MOBILE) && (
					<div
						className={css.bgMobile}
						style={{ backgroundImage: `url('${fileLink(backgroundImageMobile)}')` }}
					/>
				)}
				<div className={css.container}>
					<a href={siteLink}>
						<img src={fileLink(finalLogo)} alt="" className={css.logo} />
					</a>
					<h1 className={css.finalTitle} style={{ color: resultsTextsColor }}>
						{this.renderTitle()}
					</h1>

					{isSingle && (
						<p className={css.rangeText} style={{ color: resultsTextsColor }}>
							{this.renderSingleTitle()}
						</p>
					)}

					{isMultiple && _.size(rangesInfo) > 0 && (
						<p className={css.rangeText} style={{ color: textsColor }}>
							{this.renderRangeText()}
						</p>
					)}
					{isSharesShow && (
						<Socials
							generalShareTitle={shareTitle}
							inContentLocated
							iconsColor={sharesColor}
							title={{ titleColor: textsColor, text: sharesTitle }}
						/>
					)}
					{isShowEmailForm && <EmailForm checklistInfo={checklistInfo} emailData={this.emailData()} />}
					{isShowUserForm && <UserForm checklistInfo={checklistInfo} emailData={this.emailData()} />}
					{fileLink(bannerImg) !== '' && (
						<>
							<div className={css.bannerBlock} style={{ backgroundImage: `url(${fileLink(bannerImg)})` }}>
								<a
									href={bannerLink}
									target="_blank"
									rel="noopener noreferrer"
									onClick={this.onBottomBannerClick}
								></a>
							</div>
						</>
					)}
					{bannerText && (
						<div className={css.bannerText} dangerouslySetInnerHTML={{ __html: bannerText }}></div>
					)}
				</div>
				<div className={css.rabbiCredit} dir="ltr">
					<RabbiCredit theme="dark" location={location} />
				</div>
				<BottomLink />
				<MakoAnalytics
					pageId={MAKO_ANALYTICS_PAGE_IDS.RESULT}
					channelId={cooladataChannelId}
					cooladataId={cooladataId}
					projectURL={parentURL}
				/>
			</div>
		);
	}
}

export default withTransition(Final);
